<template>
    <!-- because it's difficult to style the choose file button of the input, Hide the default file input but keep it functional with a ref -->
    <input type="file" ref="fileInput" @change="selectedFileChange" style="display: none;">

    <div v-if="showUploadControls">
        <!-- Create a custom button to trigger the file input -->
        <v-btn color="yellow" @click="triggerFileInput">Choose File</v-btn>

        <!-- Display the selected file's name -->
        <span v-if="fileToUpload" class="file-name">{{ fileToUpload.name }}</span>
        <br>
        <div class="error" v-if="errorMsg">{{ errorMsg }}</div>
        <v-btn color="green" :disabled="!fileToUpload" @click="uploadFile">Upload</v-btn>
    </div>

    <SuccessCheckmark v-if="displayCheckmark" :display="uploadSuccess" />
    <br>
    <img :src="uploadedImageUrl" class="thumbnail" :display="showImageThumbnail" />

</template>

<script>
import SuccessCheckmark from './SuccessCheckmark.vue';

export default {
    name: 'PhotoUpload',
    props: {
        showId: {
            type: String,
            required: true
        },
        displayCheckmark: {
            type: Boolean,
            default: true
        },
        hideControlsAfterUpload: {
            type: Boolean,
            default: false
        },
    },
    components: {
        SuccessCheckmark
    },
    data() {
        return {
            fileToUpload: null,
            errorMsg: null,
            uploadSuccess: false,
            showImageThumbnail: false,
            showUploadControls: true,
            publicApiUrl: null,
            uploadedImageUrl: null,
        };
    },
    emits: [
        'photo-uploaded',
    ],
    mounted() {
        // Vue and Vite handle env variables differently. 
        // This component is used by projects initialized with Vue and Vite,
        // so both have to be supported.
        try {
            this.publicApiUrl = process.env.VUE_APP_PUB_API;
        } catch (error) {
            // despite conditional statements put in, referencing process from Vite initialized app
            // still throws an error - just ignore
        }
        try {
            if (!this.publicApiUrl) {
                this.publicApiUrl = import.meta.env.VITE_PUB_API;
            }
        } catch (error) {
            // same ignore logic as above but for Vue initialized app
        }

        if (!this.publicApiUrl) {
            throw new Error('PhotoUpload.vue -- environment variable X_PUB_API not set');
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        selectedFileChange(event) {
            var selectedFiles = Array.from(event.target.files);
            // debug... console.log("selectedFiles:\n", selectedFiles);
            // example data
            // lastModified: 1738023077162
            // lastModifiedDate: Mon Jan 27 2025 19:11:17 GMT-0500 (Eastern Standard Time) {}
            // name: "volvo-steering-wheel.jpeg"
            // size: 59973
            // type: "image/jpeg"
            const tmpFile = selectedFiles[0];
            if (tmpFile.type === 'image/jpeg' || tmpFile.type === 'image/png') {
                this.fileToUpload = tmpFile;
                this.errorMsg = null;
                console.log(`selected filename: ${this.fileToUpload.name}\nsize: ${this.fileToUpload.size} bytes`);
            } else {
                this.fileToUpload = null;
                this.errorMsg = 'Only JPEG and PNG files are allowed. Please select a different file.'
            }
        },
        async uploadFile() {
            try {
                if (!this.fileToUpload) {
                    // TODO: display error message in this case?
                    // shouldn't ever happen, but if it does would need to be fixed
                    return;
                }
                //==================================================================
                // get presigned URL from pub-api that image will be delivered to
                // (doesn't require AWS credentials to access s3 bucket 
                // from here in client-side code)
                //==================================================================
                const presignedUrlData = await this.getPresignedUrl(this.fileToUpload.name);
                //debug... console.log(`presignedUrl data:\n${JSON.stringify(presignedUrlData)}}`, presignedUrlData);

                // example data
                // { 
                //     "method": "PUT", 
                //     "url": "https://photo-prov-1-photos.s3.us-east-2.amazonaws.com/11111115-396d-491b-9c2a-bbbbbbbbbbbb--PXL_20250313_212759179.jpg", 
                //     "fields": { }, 
                //     "key": "9c33a2c2-b211-4524-a902-6c24bc48b9be--PXL_20250313_212759179.jpg" 
                // }

                //==================================================================
                // upload to s3 bucket using presigned URL
                //==================================================================
                await this.putFileInS3Bucket(presignedUrlData, this.fileToUpload);

                //==================================================================
                // on success display thumbanil of uploaded image
                //==================================================================
                this.uploadedImageUrl = presignedUrlData.previewUrl;
                this.showImageThumbnail = true;

                //==================================================================
                // on successful completion display green checkmark next to button
                //==================================================================
                this.errorMsg = null; // Clear previous errors on success
                this.uploadSuccess = true; // see style section for checkmark fade transition
                if (this.hideControlsAfterUpload) {
                    this.showUploadControls = false;
                }

                setTimeout(() => {
                    this.uploadSuccess = false;
                }, 2500); // execute after X milliseconds

                this.$emit('photo-uploaded');

            } catch (error) {
                this.errorMsg = 'Error uploading file to server';
                console.error(this.errorMsg, error);
            }
        },
        async getPresignedUrl(filename) {
            const apiEndpoint = this.publicApiUrl + '/uploads';

            try {
                const response = await fetch(apiEndpoint, {
                    method: 'POST',
                    body: JSON.stringify({
                        "filename": filename,
                        "showId": this.showId
                    }),
                    headers: {} // this is the public API so no auth header needed
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Fetch error:', response.status, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                const data = await response.json(); // Parse the JSON response body
                // debug... console.log(`fetch from ${apiEndpoint}\nresponse data:\n`, data);

                this.errorMsg = null; // Clear previous errors on success
                return data;

            } catch (error) {
                const errMsg = 'Error getting presigned URL from server';
                console.error(errMsg, error);
                throw new Error(errMsg, error);
            }
        },
        async putFileInS3Bucket(presignedUrlData, fileToUpload) {
            const s3Endpoint = presignedUrlData.uploadUrl;
            // debug... 
            console.log(`dbg - upload file ${this.fileToUpload} to ${s3Endpoint}`,);


            try {
                const response = await fetch(s3Endpoint, {
                    method: presignedUrlData.method || 'PUT',
                    body: fileToUpload,
                    headers: { // this is a presigned URL so no auth header needed
                        'Content-Type': fileToUpload.type
                    }
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Fetch error:', response.status, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                // debug...
                console.log(`Upload response:\n${JSON.stringify(response)}`, response);

            } catch (error) {
                const errMsg = 'Error putting file in S3 bucket';
                console.error(errMsg, error);
                throw new Error(errMsg, error);
            }

        }


    }
}
</script>
<style scoped>
.file-name {
    margin-left: 10px;
    font-style: italic;
    color: whitesmoke;
    background-color: #000;
}

.thumbnail {
    border: 0px dotted purple;
    max-width: 200px;
    object-fit: contain;
    padding: 10px;
}
</style>
